/** @format */

import { api } from "./client";
import type {
  SOPVersion,
  VersionStatus,
  VersionListItemRaw,
  VersionListItem,
  TitlePageContent,
} from "../types/sop";
import { normalizeVersionListItem } from "../types/sop";

export type { VersionListItem };

export interface ListVersionsResponse {
  versions: VersionListItemRaw[];
}

/**
 * Raw version shape from API (snake_case). Used for PATCH response and newDraftVersion.
 */
export type RawVersion = SOPVersion & {
  version_id?: string;
  version_num?: number;
  sop_description?: string;
  titlePage?: TitlePageContentRaw;
};

type TitlePageContentRaw = TitlePageContent & { cover_image?: string };

/**
 * Request body for completing a version (PATCH with status "done").
 * sop_description is required when completing.
 */
export interface CompleteVersionRequest {
  status: "done";
  sop_description: string;
}

/**
 * PATCH response when completing the latest version: completed version + new draft.
 */
export interface CompleteVersionResponse extends RawVersion {
  newDraftVersion?: RawVersion;
}

/**
 * GET /sops/<sop_id>/versions – lightweight list (newest first).
 */
export async function listVersions(sopId: string): Promise<VersionListItem[]> {
  const response = await api.get<ListVersionsResponse>(
    `/sops/${sopId}/versions`,
  );
  const raw = response.data?.versions ?? [];
  return raw.map(normalizeVersionListItem);
}

/**
 * GET /sops/<sop_id>/versions/<version_id> – full version payload (read-only).
 */
export async function getVersion(
  sopId: string,
  versionId: string,
): Promise<SOPVersion> {
  const response = await api.get<SOPVersion>(
    `/sops/${sopId}/versions/${versionId}`,
  );
  return response.data;
}

/**
 * PATCH /sops/<sop_id>/versions/<version_id> – complete version (status "done").
 * When the patched version is the latest, backend creates the next draft and returns it in newDraftVersion.
 */
export async function completeVersion(
  sopId: string,
  versionId: string,
  body: CompleteVersionRequest,
): Promise<CompleteVersionResponse> {
  const response = await api.patch<CompleteVersionResponse>(
    `/sops/${sopId}/versions/${versionId}`,
    body,
  );
  return response.data;
}

/**
 * POST /sops/<sop_id>/versions – create a new draft (e.g. legacy SOPs that only have a done version).
 * No body. Response: 201 with newDraftVersion.
 * Use for "Create new version" / "Start editing again"; do not use for normal completion (use PATCH with status "done" instead).
 */
export interface CreateNewDraftResponse {
  newDraftVersion: RawVersion;
}

export async function createNewDraft(
  sopId: string,
): Promise<CreateNewDraftResponse> {
  const response = await api.post<CreateNewDraftResponse>(
    `/sops/${sopId}/versions`,
  );
  return response.data;
}

/**
 * PATCH /sops/<sop_id>/versions/<version_id> – set status (e.g. draft, archived).
 * For completing the latest version and getting the new draft, use completeVersion instead.
 */
export async function setVersionStatus(
  sopId: string,
  versionId: string,
  status: VersionStatus,
): Promise<SOPVersion> {
  const response = await api.patch<SOPVersion>(
    `/sops/${sopId}/versions/${versionId}`,
    { status },
  );
  return response.data;
}
